import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const DOW = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const fmtSize = b => !b ? '—' : b > 1e9 ? (b/1e9).toFixed(2)+' GB' : b > 1e6 ? (b/1e6).toFixed(1)+' MB' : (b/1e3).toFixed(0)+' KB';

export default function BackupManagement() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState({ last:null, backups:[] });
  const [s, setS] = useState(null);
  const [secret, setSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const [view, setView] = useState('db');   // 'db' | 'images'

  const loadList = useCallback(() => { api.get('/backup').then(r => setData(r.data)).catch(()=>{}); }, []);
  const loadSettings = useCallback(() => { api.get('/backup/settings').then(r => setS(r.data)).catch(()=>{}); }, []);
  useEffect(() => { loadList(); loadSettings(); }, [loadList, loadSettings]);
  useEffect(() => {
    const onMsg = e => { if (e.data === 'drive-connected') loadSettings(); };
    window.addEventListener('message', onMsg); return () => window.removeEventListener('message', onMsg);
  }, [loadSettings]);
  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;
  if (!s) return <div style={{ color:'var(--text2)' }}>Loading…</div>;

  const set = (k,v) => setS(p => ({ ...p, [k]:v }));
  const flash = m => { setMsg(m); setTimeout(()=>setMsg(''), 2500); };

  const runNow = async () => { setBusy(true); setMsg('Backing up…'); try { await api.post('/backup/run'); loadList(); flash('✓ Backup created'); } catch(e){ flash(e.response?.data?.error||'Failed'); } finally { setBusy(false); } };
  const saveSettings = async () => {
    setBusy(true);
    try { const body = { ...s }; if (secret.trim()) body.oauth_client_secret = secret.trim(); await api.put('/backup/settings', body); setSecret(''); loadSettings(); flash('✓ Settings saved'); }
    catch(e){ flash('Save failed'); } finally { setBusy(false); }
  };
  const connectDrive = async () => {
    setBusy(true); setTestMsg('');
    try {
      // make sure the latest client id/secret are saved before requesting the consent URL
      const body = { oauth_client_id: s.oauth_client_id }; if (secret.trim()) body.oauth_client_secret = secret.trim();
      await api.put('/backup/settings', body); setSecret('');
      const r = await api.get('/backup/drive/connect');
      setRedirectUri(r.data.redirect_uri || '');
      window.open(r.data.url, 'gdrive_connect', 'width=520,height=640');
    } catch(e) { setTestMsg('✗ ' + (e.response?.data?.error || 'Could not start Google sign-in')); }
    finally { setBusy(false); }
  };
  const disconnectDrive = async () => {
    setBusy(true);
    try { await api.post('/backup/drive/disconnect'); loadSettings(); flash('Disconnected'); }
    catch(e){ flash('Failed'); } finally { setBusy(false); }
  };
  const testDrive = async () => {
    setBusy(true); setTestMsg('Testing…');
    try {
      const r = await api.post('/backup/test-drive', { drive_folder_id: s.drive_folder_id });
      setTestMsg('✓ Connected to: ' + (r.data.folder?.name || 'Drive'));
    } catch(e) { setTestMsg('✗ ' + (e.response?.data?.error || 'Test failed — check the server logs')); }
    finally { setBusy(false); }
  };
  // Fetch through axios so the auth token is sent (a plain link/window.open
  // carries no Authorization header → "No token provided"), then save the blob.
  const download = async b => {
    setBusy(true);
    try {
      const r = await api.get(`/backup/download/${b.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = b.filename || `backup-${b.id}.${b.format === 'pgdump' ? 'sql' : 'json'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert('Download failed — the backup file may no longer be on the server.');
    } finally { setBusy(false); }
  };

  const last = data.last;
  const driveColor = ds => ds==='uploaded'?'var(--green)':ds==='failed'?'var(--red)':'var(--text2)';

  const Card = ({ label, value, color }) => (
    <div className="card" style={{ flex:1, minWidth:160, textAlign:'center' }}>
      <div style={{ fontSize:18, fontWeight:700, color:color||'var(--text)' }}>{value}</div>
      <div style={{ fontSize:12, color:'var(--text2)', marginTop:4 }}>{label}</div>
    </div>
  );

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', marginBottom:16 }}>
        <div><h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>Backup Management</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Automated database backups with off-site copy to Google Drive.</p></div>
        <div className="flex-gap" style={{ gap:8, alignItems:'center' }}>
          {msg && <span style={{ fontSize:12, color:'var(--accent)' }}>{msg}</span>}
          {view==='db' && <button className="btn btn-primary" onClick={runNow} disabled={busy}>{busy?'…':'Create Backup Now'}</button>}
        </div>
      </div>

      <div className="flex-gap" style={{ gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {[['db','🗄 Database'],['images','📦 Container Images']].map(([id,l]) => (
          <button key={id} className={`btn btn-sm ${view===id?'btn-primary':'btn-secondary'}`} onClick={() => setView(id)}>{l}</button>
        ))}
      </div>

      {view==='images' && <ContainerImageBackup />}
      {view==='db' && (<>

      {/* Dashboard */}
      <div className="flex-gap" style={{ gap:12, marginBottom:22, flexWrap:'wrap' }}>
        <Card label="Last Backup" value={last ? new Date(last.created_at).toLocaleString() : '—'} />
        <Card label="Status" value={last ? (last.status==='success'?'Successful':'Failed') : '—'} color={last && last.status==='success'?'var(--green)':'var(--red)'} />
        <Card label="Size" value={last ? fmtSize(last.size_bytes) : '—'} />
        <Card label="Google Drive" value={s.drive_enabled ? 'Enabled' : 'Off'} color={s.drive_enabled?'var(--green)':'var(--text2)'} />
      </div>

      {/* Schedule */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Backup Schedule</div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <label className="opt" style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, textTransform:'none', letterSpacing:0 }}>
            <input type="checkbox" checked={s.daily_enabled} onChange={e=>set('daily_enabled',e.target.checked)} /> Daily at
            <input type="time" value={(s.daily_time||'').slice(0,5)} onChange={e=>set('daily_time',e.target.value)} style={{ padding:'4px 6px' }} />
          </label>
          <label className="opt" style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, textTransform:'none', letterSpacing:0 }}>
            <input type="checkbox" checked={s.weekly_enabled} onChange={e=>set('weekly_enabled',e.target.checked)} /> Weekly on
            <select value={s.weekly_dow} onChange={e=>set('weekly_dow',parseInt(e.target.value))} style={{ padding:'4px 6px' }}>{DOW.map((d,i)=><option key={i} value={i}>{d}</option>)}</select> at
            <input type="time" value={(s.weekly_time||'').slice(0,5)} onChange={e=>set('weekly_time',e.target.value)} style={{ padding:'4px 6px' }} />
          </label>
          <label className="opt" style={{ display:'flex', alignItems:'center', gap:10, fontSize:13, textTransform:'none', letterSpacing:0, flexWrap:'wrap' }}>
            <input type="checkbox" checked={s.monthly_enabled} onChange={e=>set('monthly_enabled',e.target.checked)} /> Monthly on
            <select value={s.monthly_lastday?'last':'day'} onChange={e=>set('monthly_lastday',e.target.value==='last')} style={{ padding:'4px 6px' }}>
              <option value="last">last day</option><option value="day">day of month</option></select>
            {!s.monthly_lastday && <input type="number" min="1" max="28" value={s.monthly_day} onChange={e=>set('monthly_day',parseInt(e.target.value)||1)} style={{ width:60, padding:'4px 6px' }} />}
            at <input type="time" value={(s.monthly_time||'').slice(0,5)} onChange={e=>set('monthly_time',e.target.value)} style={{ padding:'4px 6px' }} />
          </label>
          <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>Keep last
            <input type="number" min="1" value={s.retention_count} onChange={e=>set('retention_count',parseInt(e.target.value)||1)} style={{ width:60, padding:'4px 6px' }} /> backups (per type)</div>
        </div>
      </div>

      {/* Google Drive */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:12 }}>Google Drive (off-site copy)</div>
        <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, textTransform:'none', letterSpacing:0, marginBottom:10 }}>
          <input type="checkbox" checked={s.drive_enabled} onChange={e=>set('drive_enabled',e.target.checked)} /> Upload each backup to Google Drive
        </label>
        {/* Connection status */}
        <div className="card" style={{ background:'var(--bg)', marginBottom:12, borderLeft:`3px solid ${s.drive_connected?'var(--green)':'var(--amber)'}` }}>
          {s.drive_connected ? (
            <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
              <span style={{ fontSize:13 }}>✓ Connected as <b>{s.oauth_connected_email || 'Google account'}</b></span>
              <button className="btn btn-secondary btn-sm" onClick={disconnectDrive} disabled={busy}>Disconnect</button>
            </div>
          ) : (
            <span style={{ fontSize:13, color:'var(--text2)' }}>Not connected. Enter your OAuth Client ID + Secret below, save, then click <b>Connect Google Drive</b>.</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group"><label>OAuth Client ID</label><input value={s.oauth_client_id||''} onChange={e=>set('oauth_client_id',e.target.value)} placeholder="xxxxx.apps.googleusercontent.com" /></div>
          <div className="form-group"><label>OAuth Client Secret {s.has_oauth_secret && <span style={{ color:'var(--green)', fontSize:11 }}>· stored ✓ (leave blank to keep)</span>}</label>
            <input type="password" value={secret} onChange={e=>setSecret(e.target.value)} placeholder="GOCSPX-…" /></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label>Destination folder ID (optional)</label><input value={s.drive_folder_id||''} onChange={e=>set('drive_folder_id',e.target.value)} placeholder="folder ID from the Drive URL — blank = My Drive root" /></div>
          <div className="form-group"><label>Notification email (on failure)</label><input value={s.notify_email||''} onChange={e=>set('notify_email',e.target.value)} placeholder="itsupport@tipsglobeducate.com" /></div>
        </div>
        <div className="flex-gap" style={{ gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <button className="btn btn-primary btn-sm" onClick={connectDrive} disabled={busy || !s.oauth_client_id}>{s.drive_connected?'Reconnect':'Connect Google Drive'}</button>
          <button className="btn btn-secondary btn-sm" onClick={testDrive} disabled={busy || !s.drive_connected}>Test Connection</button>
          {testMsg && <span style={{ fontSize:12, color: testMsg.startsWith('✓') ? 'var(--green)' : testMsg==='Testing…' ? 'var(--text2)' : 'var(--red)' }}>{testMsg}</span>}
        </div>
        {redirectUri && (
          <div style={{ fontSize:11, color:'var(--text2)', marginTop:8 }}>
            If Google shows a <b>redirect_uri_mismatch</b>, add this exact URI under your OAuth client's <b>Authorized redirect URIs</b>:<br/>
            <code style={{ userSelect:'all' }}>{redirectUri}</code>
          </div>
        )}
        <div style={{ fontSize:11, color:'var(--text2)', marginTop:8 }}>
          Setup once in Google Cloud Console → <b>APIs &amp; Services → Credentials → Create OAuth client ID</b> (type: <b>Web application</b>). Enable the <b>Google Drive API</b>, add the redirect URI above, then paste the Client ID + Secret here and Save.
        </div>
      </div>

      <div style={{ marginBottom:18 }}><button className="btn btn-primary" onClick={saveSettings} disabled={busy}>Save Settings</button></div>

      {/* Backups list */}
      <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Available Backups</div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!data.backups.length ? <div className="empty-state"><p>No backups yet. Click "Create Backup Now".</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Type</th><th>Format</th><th>Size</th><th>Status</th><th>Drive</th><th></th></tr></thead>
            <tbody>{data.backups.map(b => (
              <tr key={b.id}>
                <td style={{ fontSize:12 }}>{new Date(b.created_at).toLocaleString()}</td>
                <td style={{ fontSize:12 }}>{b.type}</td>
                <td style={{ fontSize:12 }}>{b.format==='pgdump'?'SQL dump':'JSON'}</td>
                <td style={{ fontSize:12 }}>{fmtSize(b.size_bytes)}</td>
                <td><span className={`badge ${b.status==='success'?'badge-completed':'badge-faulty'}`}>{b.status}</span></td>
                <td style={{ fontSize:12, color:driveColor(b.drive_status) }}>{b.drive_status || '—'}</td>
                <td><button className="btn btn-secondary btn-sm" onClick={() => download(b)}>Download</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {/* Restore */}
      <div className="card" style={{ marginTop:16, borderLeft:'3px solid var(--amber)' }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:6 }}>Restore a Backup</div>
        <div style={{ fontSize:13, color:'var(--text2)' }}>
          Restoring overwrites all existing data, so it is performed via the documented server procedure (never from this screen) to keep your data safe:
          <ol style={{ margin:'8px 0 0 18px', fontSize:12 }}>
            <li>Download the desired <b>SQL dump</b> above (or fetch it from Google Drive).</li>
            <li>On the server: <code>./restore.sh ~/backups/&lt;file&gt;.sql</code> then <code>./deploy.sh</code>.</li>
          </ol>
          <div style={{ marginTop:8, color:'var(--amber)' }}>⚠ Always take a fresh backup before restoring.</div>
        </div>
      </div>
      </>)}
    </div>
  );
}

// ── Container Image Backup ──────────────────────────────────────────────────
function ContainerImageBackup() {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState({});
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => api.get('/image-backup').then(r => setD(r.data)).catch(()=>setD({ docker_available:false, containers:[], backups:[] })), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div style={{ color:'var(--text2)' }}>Loading…</div>;

  const toggle = n => setSel(s => ({ ...s, [n]: !s[n] }));
  const chosen = Object.keys(sel).filter(n => sel[n]);
  const flash = m => { setMsg(m); setTimeout(()=>setMsg(''), 3000); };

  const run = async () => {
    if (!chosen.length) return;
    setBusy(true); setMsg('Creating image backup… this can take a minute.');
    try { const r = await api.post('/image-backup/run', { names: chosen, label });
      setSel({}); setLabel(''); load(); flash(`✓ Saved ${r.data.file} (${fmtSize(r.data.size_bytes)})`); }
    catch (e) { flash('✗ ' + (e.response?.data?.error || 'Backup failed')); }
    finally { setBusy(false); }
  };
  const download = async f => {
    try {
      // Ask the backend for a short-lived signed URL, then let the browser
      // stream the (potentially multi-GB) tarball straight to disk.
      const r = await api.get(`/image-backup/download/${f}`);
      const a = document.createElement('a'); a.href = r.data.url; a.download = f;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { window.alert(e.response?.data?.error || 'Download failed'); }
  };
  const del = async f => { if (window.confirm(`Delete ${f}?`)) { await api.delete(`/image-backup/${f}`); load(); } };

  if (!d.docker_available) return (
    <div className="card" style={{ borderLeft:'3px solid var(--red)' }}>
      <div style={{ fontSize:13, fontWeight:600, marginBottom:6 }}>Docker not reachable</div>
      <div style={{ fontSize:13, color:'var(--text2)' }}>
        The portal can't reach the Docker daemon. The <span className="mono">/var/run/docker.sock</span> mount and
        <span className="mono"> docker-cli</span> must be present in the backend container. Rebuild with the updated
        <span className="mono"> docker-compose.yml</span> and Dockerfile, then reload.
      </div>
    </div>
  );

  return (
    <div>
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Back up containers as images</div>
        <div style={{ fontSize:12, color:'var(--text2)', marginBottom:12 }}>
          Select containers → the portal commits each to an image and saves them into one downloadable <b>.tar</b>.
          <br/><span style={{ color:'var(--amber)' }}>⚠ Image tarballs capture the container filesystem only — <b>not</b> volume data
          (Postgres DB, uploads, Ollama models). Use the Database tab for data backups.</span>
        </div>
        <div className="table-wrap" style={{ marginBottom:12 }}><table>
          <thead><tr><th style={{ width:36 }}></th><th>Container</th><th>Image</th><th>State</th></tr></thead>
          <tbody>{d.containers.map(c => (
            <tr key={c.name}>
              <td><input type="checkbox" checked={!!sel[c.name]} onChange={() => toggle(c.name)} /></td>
              <td style={{ fontWeight:500 }}>{c.name}</td>
              <td className="mono" style={{ fontSize:12 }}>{c.image}</td>
              <td><span className={`badge ${c.state==='running'?'badge-completed':'badge-pending'}`}>{c.state}</span></td>
            </tr>
          ))}</tbody>
        </table></div>
        <div className="flex-gap" style={{ gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (optional, e.g. pre-upgrade)" style={{ padding:'6px 8px', minWidth:220 }} />
          <button className="btn btn-primary" onClick={run} disabled={busy || !chosen.length}>
            {busy ? 'Working…' : `Create Image Backup${chosen.length?` (${chosen.length})`:''}`}</button>
          {msg && <span style={{ fontSize:12, color: msg.startsWith('✓')?'var(--green)':msg.startsWith('✗')?'var(--red)':'var(--text2)' }}>{msg}</span>}
        </div>
      </div>

      <div className="card" style={{ marginBottom:16, borderLeft:'3px solid var(--amber)' }}>
        <div style={{ fontSize:13, fontWeight:600, marginBottom:6 }}>How to restore an image backup</div>
        <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.6 }}>
          The <b>.tar</b> is a Docker image archive — don't extract it. On any machine with Docker, load it back:
          <div className="mono" style={{ background:'var(--bg)', padding:'6px 10px', borderRadius:'var(--radius)', margin:'6px 0', userSelect:'all' }}>docker load -i &lt;file&gt;.tar</div>
          It restores the image(s) (named <span className="mono">portal-backup/&lt;container&gt;:&lt;timestamp&gt;</span>). Retag to what
          Compose expects and start, e.g. <span className="mono">docker tag portal-backup/itportal_backend:… it-portal-backend:latest &amp;&amp; docker compose up -d</span>.
          <div style={{ marginTop:6, color:'var(--amber)' }}>⚠ Images hold the app/filesystem only — restore your <b>data</b> from the Database tab's SQL dump.</div>
        </div>
      </div>

      <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Saved Image Backups</div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!d.backups.length ? <div className="empty-state"><p>No image backups yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>File</th><th>Size</th><th>Created</th><th></th></tr></thead>
            <tbody>{d.backups.map(b => (
              <tr key={b.file}>
                <td className="mono" style={{ fontSize:12 }}>{b.file}</td>
                <td style={{ fontSize:12 }}>{fmtSize(b.size_bytes)}</td>
                <td style={{ fontSize:12 }}>{new Date(b.created_at).toLocaleString()}</td>
                <td><div className="flex-gap" style={{ gap:6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => download(b.file)}>Download</button>
                  <button className="btn btn-danger btn-sm" onClick={() => del(b.file)}>Delete</button>
                </div></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
